// Microbe RTS — DOM HUD (portrait, one-thumb).
//
// mountHud builds a read-only top bar + a thumb-arc bottom dock and keeps them in sync with the
// sim on a ~5/s timer. It NEVER mutates game state directly: player intent goes out as sim.command,
// and cross-UI intent (open evolve sheet, recenter camera, arm rally) rides window CustomEvents so
// the HUD stays decoupled from the input + evolve mounts (see input.ts / evolve.ts headers).
//
//   OUT 'microbe:evolve-open'      {}           -> evolve.ts opens the mutation sheet
//   OUT 'microbe:evolve-quickbuy'  {}           -> evolve.ts buys the recommended (first offered) mutation
//   OUT 'microbe:rally' detail     { nodeId }   -> input.ts arms "next tap sets this node's rally"
//   OUT 'microbe:camlock-toggle'   {}           -> input.ts flips the camera-follow lock
//   OUT 'microbe:home-cycle'       {}           -> input.ts jumps to home, then cycles owned craters
//   OUT 'microbe:help'             {}           -> main.ts opens the how-to-play sheet
//   IN  'microbe:nodeselect' detail{ nodeId }   -> input.ts told us which node panel to show
//   IN  'microbe:camlock' detail   { on }       -> input.ts echoes lock state so the button reflects it
//
// Element ids (documented so main.ts/the stylesheet can target them):
//   #hud-topbar #hud-pause #hud-energy #hud-amino #hud-toxin #hud-timer #hud-pop #hud-tier
//   #hud-dock #hud-selreadout #hud-groups #hud-evolve #hud-actions
//   #hud-controls #hud-help #hud-camlock #hud-home   (the narrow bottom control bar)
// Class hooks used for styling/state: .hud-res .hud-trend .amber .red .chip .chip-disabled
//   .chip-on .produce-chip .group-chip .stance-chip .evolve-chip .ctrl-btn .pause-btn

import {
  UNITS,
  HEALER,
  HEALER_UNLOCK_TIER,
  POP_WEIGHT,
  MUTATIONS,
  NODE_SPECS,
  BROOD_COST,
  SEED_COST,
  TURRET,
} from '../config.js';
import type { UnitSpec } from '../config.js';
import type { Sim, WarriorClass, UnitKind, WorldNode, BandStance, Unit, Side } from '../types.js';
import { iconSvg, UNIT_ICON, MUT_ICON } from './icons.js';

const WARRIORS: WarriorClass[] = ['lancer', 'phalanx', 'stinger', 'drone', 'engulfer', 'toxifer'];
const BASE_WARRIORS: WarriorClass[] = ['lancer', 'phalanx', 'stinger', 'drone'];

/** Spec for any producible kind (healer / warrior). */
function specOf(kind: UnitKind): UnitSpec {
  if (kind === 'healer') return HEALER;
  return UNITS[kind as WarriorClass];
}
/** Inline icon + count, e.g. a mini roster tag. */
function iconTag(kind: UnitKind, n: number): string {
  return `${iconSvg(UNIT_ICON[kind])}${n}`;
}
/** Human-readable class name for the selection title bar. */
const KIND_NAME: Record<UnitKind, string> = {
  lancer: 'Lancer', phalanx: 'Phalanx', stinger: 'Stinger',
  engulfer: 'Engulfer', toxifer: 'Toxifer', drone: 'Drone', healer: 'Healer', grazer: 'Grazer',
};
/** Display name: a crowned drone reads as "Queen" (kind stays 'drone' after the seed promotes her). */
function unitName(u: Unit): string {
  return u.queen ? 'Queen' : KIND_NAME[u.kind];
}
function hpClass(frac: number): string {
  return frac > 0.5 ? 'hp-ok' : frac > 0.25 ? 'hp-warn' : 'hp-low';
}
const STANCES: BandStance[] = ['hold', 'aggressive', 'skirmish'];
const STANCE_LABEL: Record<BandStance, string> = {
  hold: 'Hold',
  aggressive: 'Aggro',
  skirmish: 'Skirmish',
};

/** Trend glyph from a signed per-second rate (NOT a "+x/s" number, per the bible). */
function trendGlyph(rate: number): string {
  const eps = 0.05;
  if (rate > eps) return '▲';
  if (rate < -eps) return '▼';
  return '▬';
}

function fmt(n: number): string {
  return Math.floor(n).toString();
}

function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/** Which warrior classes can this colony currently produce (base classes + keystone unlocks). */
function unlockedWarriors(sim: Sim, side: Side): Set<WarriorClass> {
  const set = new Set<WarriorClass>(BASE_WARRIORS);
  const col = sim.state.colonies[side];
  for (const m of MUTATIONS) {
    const cls = m.effect?.unlockClass;
    if (!cls) continue;
    const owned = (col.genome[m.id] ?? 0) > 0 || col.keystones.includes(m.id);
    if (owned) set.add(cls);
  }
  return set;
}

/** The full producible roster: unlocked warriors (incl. drone), + healer once Tier III. */
function producibleKinds(sim: Sim, side: Side): UnitKind[] {
  const unlocked = unlockedWarriors(sim, side);
  const kinds: UnitKind[] = [...WARRIORS.filter((w) => unlocked.has(w))];
  if (sim.state.colonies[side].tier >= HEALER_UNLOCK_TIER) kinds.push('healer');
  return kinds;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  id?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (id) e.id = id;
  return e;
}

export function mountHud(root: HTMLElement, sim: Sim, viewSide: Side = 'you'): { refresh(): void } {
  // The enemy side, from this client's POV — used only for enemy-facing labels (never the sim/hash).
  const foeSide: Side = viewSide === 'you' ? 'bot' : 'you';
  // Which node's panel the dock shows (fed by input.ts via 'microbe:nodeselect').
  let selectedNodeId: string | null = null;
  // The control panel (build/produce/evolve/groups) is a POPUP: it shows ONLY while one of your
  // own nodes (home or colony) is selected, and takes over the dock — the joystick + recenter FAB
  // hide while it's open (and the minimap hides too, via the 'microbe:panel' event that render.ts
  // listens for). During normal play the dock is just the floating joystick + return-home FAB.
  let panelOpen = false;
  // Content signatures — the interactive chip rows (groups + actions) only rebuild when their
  // content changes; rebuilding every refresh (~5-10/s) was destroying chips mid-tap, so produce
  // and stance taps only landed intermittently. (The top bar + readout still update in place.)
  let lastGroupsSig = '';
  let lastActionsSig = '';

  // ---------------- TOP BAR (read-only) ----------------
  const topbar = el('div', 'hud-topbar', 'hud-topbar');

  const pauseBtn = el('button', 'pause-btn', 'hud-pause');
  pauseBtn.type = 'button';
  pauseBtn.innerHTML = iconSvg('pause');
  pauseBtn.setAttribute('aria-label', 'Pause');
  pauseBtn.addEventListener('click', () => {
    sim.command({ t: 'pause', on: !sim.state.clocks.paused });
    render(); // reflect immediately
  });

  const mkRes = (id: string, svgName: 'energy' | 'amino' | 'toxin') => {
    const wrap = el('div', 'hud-res', id);
    const ic = el('span', 'hud-res-icon');
    ic.innerHTML = iconSvg(svgName);
    const val = el('span', 'hud-res-val');
    const trend = el('span', 'hud-trend');
    wrap.append(ic, val, trend);
    return { wrap, val, trend };
  };
  const energy = mkRes('hud-energy', 'energy');
  const amino = mkRes('hud-amino', 'amino');
  const toxin = mkRes('hud-toxin', 'toxin');

  const timer = el('div', 'hud-timer', 'hud-timer');
  const pop = el('div', 'hud-pop', 'hud-pop');
  const tier = el('div', 'hud-tier', 'hud-tier');

  topbar.append(pauseBtn, energy.wrap, amino.wrap, toxin.wrap, timer, pop, tier);

  // ---------------- BOTTOM DOCK (thumb arc) ----------------
  const dock = el('div', 'hud-dock', 'hud-dock');

  // Selection readout doubles as the DESELECT control: tap it to clear the current selection.
  const selReadout = el('button', 'hud-selreadout', 'hud-selreadout');
  selReadout.type = 'button';
  Object.assign(selReadout.style, {
    background: 'transparent',
    border: '0',
    textAlign: 'left',
    cursor: 'pointer',
    width: '100%',
  } as Partial<CSSStyleDeclaration>);
  selReadout.addEventListener('click', () => {
    if (sim.state.selectionBySide[viewSide].length === 0) return;
    sim.command({ t: 'clearSelection' });
    selectedNodeId = null;
    window.dispatchEvent(new CustomEvent('microbe:nodeselect', { detail: { nodeId: null } }));
    render();
  });
  const groups = el('div', 'hud-groups', 'hud-groups');
  const actions = el('div', 'hud-actions', 'hud-actions');

  // persistent EVOLVE chip (>=64px) — opens the sheet; long body shows the recommendation.
  const evolveChip = el('button', 'chip evolve-chip', 'hud-evolve');
  evolveChip.type = 'button';
  const evolveMain = el('span', 'evolve-main');
  evolveMain.innerHTML = `${iconSvg('evolve')} EVOLVE`;
  const evolveRec = el('span', 'evolve-rec'); // recommendation sublabel
  evolveChip.append(evolveMain, evolveRec);
  evolveChip.addEventListener('click', () =>
    window.dispatchEvent(new CustomEvent('microbe:evolve-open')),
  );
  // A small quick-buy affordance for the recommended mutation (one-tap).
  const evolveQuick = el('button', 'chip evolve-quick', 'hud-evolve-quick');
  evolveQuick.type = 'button';
  evolveQuick.innerHTML = iconSvg('plus');
  evolveQuick.setAttribute('aria-label', 'Buy recommended mutation');
  evolveQuick.addEventListener('click', (ev) => {
    ev.stopPropagation();
    window.dispatchEvent(new CustomEvent('microbe:evolve-quickbuy'));
  });

  // ---------------- NARROW BOTTOM CONTROL BAR (persistent during play) ----------------
  // A slim, thumb-height strip of GLOBAL camera controls: help · camera-lock · home-cycle.
  //   help        -> 'microbe:help'          (main.ts opens the how-to-play sheet)
  //   camera-lock -> 'microbe:camlock-toggle'(input.ts flips follow; echoes 'microbe:camlock' {on})
  //   home        -> 'microbe:home-cycle'    (input.ts jumps camera home, then cycles owned craters)
  // Hidden while a node control panel is open (the panel owns the bottom then).
  const controls = el('div', 'hud-controls', 'hud-controls');
  let camLocked = false;

  const ctrlHelp = el('button', 'ctrl-btn ctrl-help', 'hud-help');
  ctrlHelp.type = 'button';
  ctrlHelp.innerHTML = '?';
  ctrlHelp.setAttribute('aria-label', 'How to play');
  ctrlHelp.addEventListener('click', () => window.dispatchEvent(new CustomEvent('microbe:help')));

  const lockBtn = el('button', 'ctrl-btn ctrl-lock', 'hud-camlock');
  lockBtn.type = 'button';
  lockBtn.innerHTML = iconSvg('unlock');
  lockBtn.setAttribute('aria-label', 'Lock camera to selection');
  lockBtn.addEventListener('click', () =>
    window.dispatchEvent(new CustomEvent('microbe:camlock-toggle')),
  );

  const homeBtn = el('button', 'ctrl-btn ctrl-home', 'hud-home');
  homeBtn.type = 'button';
  homeBtn.innerHTML = iconSvg('home');
  homeBtn.setAttribute('aria-label', 'Jump to home, then cycle craters');
  homeBtn.addEventListener('click', () =>
    window.dispatchEvent(new CustomEvent('microbe:home-cycle')),
  );

  // Sibling of the home-cycle button: same round-robin, but over your Queens/drones instead of craters.
  const queenBtn = el('button', 'ctrl-btn ctrl-queen', 'hud-queen');
  queenBtn.type = 'button';
  queenBtn.innerHTML = iconSvg('drone');
  queenBtn.setAttribute('aria-label', 'Cycle through your queens and drones');
  queenBtn.addEventListener('click', () =>
    window.dispatchEvent(new CustomEvent('microbe:queen-cycle')),
  );

  controls.append(ctrlHelp, lockBtn, homeBtn, queenBtn);

  // input.ts owns the lock state; it echoes 'microbe:camlock' {on} so we reflect the button here.
  const onCamLock = (e: Event) => {
    camLocked = !!(e as CustomEvent<{ on: boolean }>).detail?.on;
    lockBtn.classList.toggle('chip-on', camLocked);
    lockBtn.innerHTML = iconSvg(camLocked ? 'lock' : 'unlock');
  };
  window.addEventListener('microbe:camlock', onCamLock);

  // Compact bottom cluster (~1/3 screen): left column = readout + evolve + produce + groups;
  // right column = recenter FAB stacked over the circular movement joystick pad.
  const evolveRow = el('div', 'hud-evolve-row');
  evolveRow.append(evolveChip, evolveQuick);

  // Close affordance for the popup — taps clear the node selection, which drops panelOpen and
  // returns the dock to the floating joystick + FAB (and re-shows the minimap).
  const panelClose = el('button', 'panel-close', 'hud-panel-close');
  panelClose.type = 'button';
  panelClose.innerHTML = iconSvg('close');
  panelClose.setAttribute('aria-label', 'Close panel');
  panelClose.addEventListener('click', () => {
    selectedNodeId = null;
    window.dispatchEvent(new CustomEvent('microbe:nodeselect', { detail: { nodeId: null } }));
    render();
  });
  const leftHead = el('div', 'hud-left-head');
  leftHead.append(selReadout, panelClose);

  // Production progress (staggered) — head unit's kind + a live loading bar + queued count.
  const prodBar = el('div', 'hud-prodbar', 'hud-prodbar');
  prodBar.hidden = true;
  const prodBarLabel = el('span', 'prodbar-label');
  const prodBarTrack = el('div', 'prodbar-track');
  const prodBarFill = el('div', 'prodbar-fill');
  prodBarTrack.append(prodBarFill);
  prodBar.append(prodBarLabel, prodBarTrack);

  const left = el('div', 'hud-left');
  left.append(leftHead, evolveRow, prodBar, actions, groups);

  // The camera joystick is now DYNAMIC (ui/floating-joystick.ts, spawned by ui/input.ts on an
  // open-water touch), and the return-home FAB moved into the persistent bottom control bar
  // (built above) — so during normal play the dock holds only the (hidden) node panel column.

  // Deselect control — a floating pill shown during normal play whenever units are selected (the
  // in-panel readout is hidden then). Tap clears the selection.
  const deselectChip = el('button', 'deselect-chip', 'hud-deselect');
  deselectChip.type = 'button';
  deselectChip.hidden = true;
  deselectChip.addEventListener('click', () => {
    if (sim.state.selectionBySide[viewSide].length === 0) return;
    sim.command({ t: 'clearSelection' });
    render();
  });

  // Selected-unit TITLE BAR — one pill per selected unit (name + live HP), pinned just under the
  // topbar. Body-level + pointer-events:none so it never swallows a map tap (the #hud > * rule
  // would otherwise force it interactive).
  const selTitle = el('div', 'hud-seltitle', 'hud-seltitle');
  selTitle.hidden = true;

  dock.append(left);
  root.append(topbar, dock, controls);
  // Body-level (not inside #hud's z-40 stacking context) so its z-index really sits above the
  // floating joystick pad (also a body child).
  document.body.appendChild(deselectChip);
  document.body.appendChild(selTitle);

  // ---- nodeselect wiring (from input.ts) ----
  const onNodeSelect = (e: Event) => {
    selectedNodeId = (e as CustomEvent<{ nodeId: string | null }>).detail?.nodeId ?? null;
    render();
  };
  window.addEventListener('microbe:nodeselect', onNodeSelect);

  // ---------------- render helpers ----------------
  function canAfford(cost: { energy: number; amino: number; toxin: number }): boolean {
    const r = sim.state.colonies[viewSide].res;
    return r.energy >= cost.energy && r.amino >= cost.amino && r.toxin >= cost.toxin;
  }

  function renderTopbar() {
    const col = sim.state.colonies[viewSide];
    const rate = sim.rates(viewSide);
    const c = sim.state.clocks;

    energy.val.textContent = fmt(col.res.energy);
    energy.trend.textContent = trendGlyph(rate.energy);
    amino.val.textContent = fmt(col.res.amino);
    amino.trend.textContent = trendGlyph(rate.amino);
    toxin.val.textContent = fmt(col.res.toxin);
    toxin.trend.textContent = trendGlyph(rate.toxin);

    timer.textContent = clock(c.sim);
    timer.classList.toggle('amber', c.oxyActive);

    pop.textContent = `${col.popUsed}/${col.popCap}`;
    pop.classList.toggle('red', col.popUsed >= col.popCap);

    tier.textContent = `T${col.tier}`;

    pauseBtn.innerHTML = iconSvg(c.paused ? 'play' : 'pause');
    pauseBtn.classList.toggle('chip-on', c.paused);
  }

  function selectedUnitsComposition(): string {
    const counts = new Map<UnitKind, number>();
    for (const id of sim.state.selectionBySide[viewSide]) {
      const u = sim.unitById(id);
      if (!u) continue;
      counts.set(u.kind, (counts.get(u.kind) ?? 0) + 1);
    }
    if (counts.size === 0) return '';
    const parts: string[] = [];
    for (const [k, n] of counts) parts.push(iconTag(k, n));
    return parts.join(' ');
  }

  function renderSelReadout(node: WorldNode | null) {
    if (node) {
      const spec = NODE_SPECS[node.type];
      const owner =
        node.owner === viewSide ? 'Yours' : node.owner === foeSide ? 'Rival' : 'Neutral';
      const auto = node.owner === viewSide && spec.workerCap > 0 ? ' · auto-working' : '';
      // Neutral crater: surface the seed cost. Your FIRST colony (no home yet) — or any home
      // crater — is free; everything after costs SEED_COST.
      let seed = '';
      if (node.owner === 'neutral') {
        const hasHome = !!sim.nodeById(sim.state.colonies[viewSide].homeId);
        seed = (!hasHome || node.type === 'home')
          ? ' · seed: free → Queen'
          : ` · seed ${iconSvg('energy')}${SEED_COST.energy} ${iconSvg('amino')}${SEED_COST.amino}`;
      }
      selReadout.innerHTML = `${node.type.toUpperCase()} · ${owner}${auto}${seed}`;
      return;
    }
    const comp = selectedUnitsComposition();
    const n = sim.state.selectionBySide[viewSide].length;
    selReadout.innerHTML = n > 0
      ? `${iconSvg('close')} ${n} selected · ${comp} · tap to clear`
      : 'Tap a unit to select · double-tap for all of a kind';
  }

  function renderGroups() {
    const gs = sim.state.groupsBySide[viewSide];
    const hasSel = sim.state.selectionBySide[viewSide].length > 0;
    const sig = [0, 1, 2, 3].map((i) => (gs[i] ?? []).length).join(',') + (hasSel ? '|s' : '');
    if (sig === lastGroupsSig && groups.childElementCount > 0) return;
    lastGroupsSig = sig;
    groups.replaceChildren();
    for (let i = 0; i < 4; i++) {
      const ids = gs[i] ?? [];
      const chip = el('button', 'chip group-chip');
      chip.type = 'button';
      if (ids.length > 0) {
        // Tap to re-select this saved group.
        chip.textContent = `${i + 1}·${ids.length}`;
        chip.addEventListener('click', () => {
          sim.command({ t: 'select', ids: ids.slice() });
          selectedNodeId = null;
          window.dispatchEvent(new CustomEvent('microbe:nodeselect', { detail: { nodeId: null } }));
          render();
        });
      } else if (hasSel) {
        // Empty slot + a live selection: tap to BANK the current selection into this group.
        chip.textContent = `${i + 1}·＋`;
        chip.addEventListener('click', () => {
          sim.command({ t: 'assignGroup', slot: i });
          render();
        });
      } else {
        // Empty slot, nothing to bank.
        chip.textContent = `${i + 1}·–`;
        chip.classList.add('chip-disabled');
      }
      groups.append(chip);
    }
  }

  function produceChip(node: WorldNode, kind: UnitKind) {
    const spec = specOf(kind);
    const col = sim.state.colonies[viewSide];
    const affordable = canAfford(spec.cost);
    const popRoom = col.popUsed + (POP_WEIGHT[kind] ?? 1) <= col.popCap;
    const ok = affordable && popRoom;

    const chip = el('button', 'chip produce-chip');
    chip.type = 'button';
    const label = el('span', 'chip-label');
    label.innerHTML = `${iconSvg(UNIT_ICON[kind])} ${kind}`;
    const cost = el('span', 'chip-cost');
    const bits = [`${iconSvg('energy')}${spec.cost.energy}`, `${iconSvg('amino')}${spec.cost.amino}`];
    if (spec.cost.toxin > 0) bits.push(`${iconSvg('toxin')}${spec.cost.toxin}`);
    cost.innerHTML = bits.join(' ');
    chip.append(label, cost);
    chip.classList.toggle('chip-disabled', !ok);
    if (ok) {
      chip.addEventListener('click', () => {
        sim.command({ t: 'produce', nodeId: node.id, kind });
        render();
      });
    }
    return chip;
  }

  // The node we build at: an explicitly-selected own node, else your Home (primary brood).
  function buildTarget(): WorldNode | null {
    if (selectedNodeId) {
      const n = sim.nodeById(selectedNodeId);
      if (n && n.owner === viewSide) return n;
    }
    return sim.nodeById(sim.state.colonies[viewSide].homeId) ?? null;
  }

  function renderActions() {
    const col = sim.state.colonies[viewSide];
    const explicitNode = selectedNodeId ? sim.nodeById(selectedNodeId) ?? null : null;
    const ownExplicit = explicitNode && explicitNode.owner === viewSide ? explicitNode : null;

    const sel = sim.state.selectionBySide[viewSide].map((id) => sim.unitById(id));
    const hasWarrior = sel.some((u) => u && u.kind !== 'healer');

    // Produce chips show on an explicit own node, OR by default when no warriors are selected —
    // so warrior production for your Home is ALWAYS one tap away in the dock.
    const target = buildTarget();
    const showProduce = !!target && target.hasBrood && (!!ownExplicit || !hasWarrior);

    // ---- signature (skip rebuild if unchanged, so a chip isn't pulled out from under a tap) ----
    const parts: string[] = [];
    if (showProduce && target) {
      parts.push(
        `prod:${target.id}:` +
          producibleKinds(sim, viewSide)
            .map((k) => {
              const spec = specOf(k);
              const ok = canAfford(spec.cost) && col.popUsed + (POP_WEIGHT[k] ?? 1) <= col.popCap;
              return `${k}${ok ? 1 : 0}`;
            })
            .join(','),
      );
    }
    const needsBrood = !!ownExplicit && !ownExplicit.hasBrood && ownExplicit.type !== 'home';
    if (ownExplicit) parts.push(`node:${ownExplicit.id}`);
    if (needsBrood) parts.push(`brood:${canAfford(BROOD_COST) ? 1 : 0}`);
    if (hasWarrior) {
      const first = sel.find((u) => u && u.kind !== 'healer');
      parts.push(`stance:${first?.bandStance ?? ''}`);
    }
    // Home turrets: Build (arms tap-place) + Repair chips. Surface on the HOME by DEFAULT (same rule
    // as the produce chips) — not only on an explicit home tap — so after buying the Sessile Fortress
    // the placement chip is always one tap away in the dock instead of hidden. target = explicit own
    // node, else Home; atHomeDock is true only when that target IS the home and we're not mid warrior-command.
    const atHomeDock = !!target && target.id === col.homeId && (!!ownExplicit || !hasWarrior);
    const turretUnlocked = atHomeDock && sim.turretsUnlocked(viewSide);
    let okTurret = false;
    let showRepair = false;
    let okRepair = false;
    const repairCost = { energy: 0, amino: 0, toxin: 0 };
    if (turretUnlocked) {
      const cap = sim.turretCap(viewSide);
      const mine = sim.state.turrets.filter((t) => t.side === viewSide);
      okTurret =
        mine.length < cap &&
        canAfford({ energy: TURRET.cost.energy, amino: TURRET.cost.amino, toxin: TURRET.cost.toxin });
      for (const t of mine) {
        if (t.timer > 0 || t.hp >= t.maxHp) continue;
        const missing = 1 - Math.max(0, t.hp) / t.maxHp;
        repairCost.energy += TURRET.cost.energy * missing;
        repairCost.amino += TURRET.cost.amino * missing;
      }
      showRepair = repairCost.energy > 0 || repairCost.amino > 0;
      okRepair = showRepair && canAfford(repairCost);
      // Booleans only in the sig — NOT the live repair cost (which drifts every frame while a turret is
      // being ground down, thrashing the chip row and dropping in-progress taps). okRepair is included so
      // the row repaints when repair affordability flips.
      parts.push(`turret:${okTurret ? 1 : 0}:${mine.length}:${cap}:${showRepair ? 1 : 0}:${okRepair ? 1 : 0}`);
    }
    const sig = parts.join('|') || 'empty';
    if (sig === lastActionsSig && actions.childElementCount > 0) return;
    lastActionsSig = sig;
    actions.replaceChildren();

    // ---- produce chips (Home by default, or the selected forward brood) ----
    if (showProduce && target) {
      for (const k of producibleKinds(sim, viewSide)) actions.append(produceChip(target, k));
    }

    // ---- an explicitly-selected own node: Build-Brood (if none) + Rally ----
    if (ownExplicit) {
      if (needsBrood) {
        // A captured colony can't produce until it grows a Brood — surface that here.
        const okBrood = canAfford(BROOD_COST);
        const brood = el('button', 'chip stance-chip');
        brood.type = 'button';
        brood.innerHTML = `${iconSvg('egg')} Build Brood ${iconSvg('energy')}${BROOD_COST.energy} ${iconSvg('amino')}${BROOD_COST.amino}`;
        brood.classList.toggle('chip-disabled', !okBrood);
        if (okBrood) {
          brood.addEventListener('click', () => {
            sim.command({ t: 'buildBrood', nodeId: ownExplicit.id });
            render();
          });
        }
        actions.append(brood);
      }
      const rally = el('button', 'chip stance-chip');
      rally.type = 'button';
      rally.innerHTML = `${iconSvg('rally')} Rally`;
      rally.addEventListener('click', () =>
        window.dispatchEvent(
          new CustomEvent('microbe:rally', { detail: { nodeId: ownExplicit.id } }),
        ),
      );
      actions.append(rally);
    }

    // ---- Home turrets: Build (tap-to-place) + Repair. Rendered OUTSIDE the explicit-selection block
    //      so the Sessile Fortress placement chip surfaces by default whenever you're on Home (see
    //      turretUnlocked/atHomeDock above), matching the produce chips. ----
    if (turretUnlocked) {
      const turret = el('button', 'chip stance-chip');
      turret.type = 'button';
      turret.innerHTML = `${iconSvg('target')} Turret ${iconSvg('energy')}${TURRET.cost.energy} ${iconSvg('amino')}${TURRET.cost.amino}`;
      turret.classList.toggle('chip-disabled', !okTurret);
      if (okTurret) {
        turret.addEventListener('click', () => {
          // Arm placement, then close the panel so the next map tap lands on open water (the
          // placement tap) instead of the panel chrome.
          window.dispatchEvent(new CustomEvent('microbe:place-turret'));
          selectedNodeId = null;
          window.dispatchEvent(new CustomEvent('microbe:nodeselect', { detail: { nodeId: null } }));
          render();
        });
      }
      actions.append(turret);

      if (showRepair) {
        const repair = el('button', 'chip stance-chip');
        repair.type = 'button';
        repair.innerHTML = `${iconSvg('rally')} Repair ${iconSvg('energy')}${Math.ceil(repairCost.energy)} ${iconSvg('amino')}${Math.ceil(repairCost.amino)}`;
        repair.classList.toggle('chip-disabled', !okRepair);
        if (okRepair) {
          repair.addEventListener('click', () => {
            sim.command({ t: 'repairTurrets' });
            render();
          });
        }
        actions.append(repair);
      }
    }

    // ---- warrior stance toggle ----
    if (hasWarrior) {
      const first = sel.find((u) => u && u.kind !== 'healer');
      const current = first?.bandStance;
      for (const st of STANCES) {
        const chip = el('button', 'chip stance-chip');
        chip.type = 'button';
        chip.textContent = STANCE_LABEL[st];
        chip.classList.toggle('chip-on', current === st);
        chip.addEventListener('click', () => {
          sim.command({ t: 'stance', stance: st });
          render();
        });
        actions.append(chip);
      }
    }
  }

  function renderProdBar() {
    // The bar tracks the node whose panel is open (buildTarget = selected own node, else Home).
    const target = buildTarget();
    const jobs = target && target.owner === viewSide ? sim.production(target.id) : [];
    if (jobs.length === 0) {
      prodBar.hidden = true;
      return;
    }
    prodBar.hidden = false;
    const head = jobs[0];
    const queued = jobs.length > 1 ? ` ·${jobs.length}` : '';
    prodBarLabel.innerHTML = `${iconSvg(UNIT_ICON[head.kind])} ${head.kind}${queued}`;
    const pct = Math.round(Math.max(0, Math.min(1, head.fraction)) * 100);
    prodBarFill.style.width = `${pct}%`;
  }

  function renderDeselect() {
    const n = sim.state.selectionBySide[viewSide].length;
    const show = n > 0 && !panelOpen;
    deselectChip.hidden = !show;
    if (show) deselectChip.innerHTML = `${iconSvg('close')} ${n} selected`;
  }

  // One pill per selected unit: its class NAME + live HP (ceil'd), colored by health fraction.
  // Kept low so the shown pills + the "+N" summary always fit the bar's ~2-row clamp even on a narrow
  // phone — beyond this we summarize with a "+N" pill instead of letting the list clip silently.
  const SEL_TITLE_CAP = 10;
  function renderSelTitle() {
    const ids = sim.state.selectionBySide[viewSide];
    if (ids.length === 0) {
      if (!selTitle.hidden) { selTitle.hidden = true; selTitle.replaceChildren(); }
      return;
    }
    const units = [];
    for (const id of ids) {
      const u = sim.unitById(id);
      if (u) units.push(u);
    }
    if (units.length === 0) {
      if (!selTitle.hidden) { selTitle.hidden = true; selTitle.replaceChildren(); }
      return;
    }
    selTitle.hidden = false;
    // Past a handful of units go COMPACT — icon + current HP only, no class name or /max — so many more
    // pills fit per row and the bar stays contained to its ~2-row cap instead of bleeding down.
    const compact = units.length > 5;
    const frag = document.createDocumentFragment();
    for (const u of units.slice(0, SEL_TITLE_CAP)) {
      const frac = u.maxHp > 0 ? u.hp / u.maxHp : 1;
      const pill = el('span', `seltitle-pill ${hpClass(frac)}${compact ? ' seltitle-compact' : ''}`);
      pill.innerHTML = compact
        ? `${iconSvg(UNIT_ICON[u.kind])}` +
          `<span class="seltitle-hp">${Math.max(0, Math.ceil(u.hp))}</span>`
        : `${iconSvg(UNIT_ICON[u.kind])}` +
          `<span class="seltitle-name">${unitName(u)}</span>` +
          `<span class="seltitle-hp">${Math.max(0, Math.ceil(u.hp))}/${Math.round(u.maxHp)}</span>`;
      frag.append(pill);
    }
    if (units.length > SEL_TITLE_CAP) {
      const more = el('span', 'seltitle-pill seltitle-more');
      more.textContent = `+${units.length - SEL_TITLE_CAP}`;
      frag.append(more);
    }
    selTitle.replaceChildren(frag);
  }

  function renderEvolveRec() {
    const offered = sim.offeredMutations(viewSide);
    const recId = offered[0];
    if (!recId) {
      evolveRec.textContent = '';
      evolveQuick.classList.add('chip-disabled');
      evolveQuick.disabled = true;
      return;
    }
    const m = MUTATIONS.find((x) => x.id === recId);
    evolveRec.innerHTML = m ? `${iconSvg(MUT_ICON[m.id] ?? 'evolve')} ${m.name}` : '';
    evolveQuick.classList.remove('chip-disabled');
    evolveQuick.disabled = false;
  }

  function render() {
    renderTopbar();
    const node = selectedNodeId ? sim.nodeById(selectedNodeId) ?? null : null;
    if (selectedNodeId && !node) selectedNodeId = null; // node vanished (captured/destroyed)
    // The control panel opens ONLY for one of your own nodes (home or colony). Neutral/enemy nodes
    // and bare unit selections keep the normal (joystick + FAB) dock. Toggle on change only.
    const open = !!node && node.owner === viewSide;
    if (open !== panelOpen) {
      panelOpen = open;
      dock.classList.toggle('panel-open', open);
      controls.hidden = open; // the node panel owns the bottom while it's up
      window.dispatchEvent(new CustomEvent('microbe:panel', { detail: { open } }));
    }
    // Camera-lock only bites with units selected — dim the toggle when there's nothing to follow.
    lockBtn.classList.toggle('ctrl-idle', !camLocked && sim.state.selectionBySide[viewSide].length === 0);
    renderSelReadout(node);
    renderGroups();
    renderActions();
    renderProdBar();
    renderDeselect();
    renderSelTitle();
    renderEvolveRec();
  }

  render();
  const timerId = window.setInterval(render, 200); // ~5/s

  // Cleanup: stop the 5 Hz repaint + drop the window listeners. Exposed as destroy() (called when a
  // match ends and the replay takes over) and also stashed on the element for legacy hosts.
  const cleanup = (): void => {
    window.clearInterval(timerId);
    window.removeEventListener('microbe:nodeselect', onNodeSelect);
    window.removeEventListener('microbe:camlock', onCamLock);
  };
  (dock as unknown as { _cleanup?: () => void })._cleanup = cleanup;

  return { refresh: render, destroy: cleanup };
}
